import { jsonInit } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import { targetingIabCategoriesSchema } from '../../targeting/apiSchemas';
import {
  placementDemandIntegrationsPageSchema,
  placementLookupPageSchema,
  placementSchema,
  placementStatusCountsSchema,
  serializePlacementStatusCountsFilter,
  placementTargetingsPageSchema,
  placementsPageSchema,
  serializePlacementLookupFilter,
  serializePlacementsFilter,
  type PlacementLookupFilter,
  type PlacementStatusCountsFilter,
  type PlacementsFilter,
} from './model';
import type { PlacementMutationPayload } from './placementFormModel';

interface PlacementsClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const placementMutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.union([z.void(), placementSchema]),
);
const placementActionResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.union([z.void(), z.looseObject({})]),
);
const adUnitConfigSchema = z.looseObject({ placementId: z.string().nullish() });
const widgetTypesPageSchema = z.looseObject({
  content: z.array(
    z.looseObject({
      id: z.union([z.string(), z.number()]).transform(String),
      name: z.string().optional(),
      displayName: z.string(),
    }),
  ),
  totalElements: z.number(),
});
const publisherDomainsPageSchema = z.looseObject({
  content: z.array(
    z
      .looseObject({
        id: z.looseObject({ domainId: z.union([z.string(), z.number()]).transform(String) }),
        domain: z
          .looseObject({
            url: z.string().nullish(),
            name: z.string().nullish(),
          })
          .nullish(),
      })
      .transform((item) => ({
        id: item.id.domainId,
        domain: item.domain?.url ?? item.domain?.name ?? item.id.domainId,
      })),
  ),
  totalElements: z.number(),
});

export function createPlacementsClient({ config, accessToken, fetcher }: PlacementsClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    list: (filter: PlacementsFilter, options?: { signal?: AbortSignal }) =>
      api.request({
        path: `/api/placements?${serializePlacementsFilter(filter)}`,
        schema: placementsPageSchema,
        ...(options?.signal ? { init: { signal: options.signal } } : {}),
      }),
    getStatusCounts: (filter: PlacementStatusCountsFilter) =>
      api.request({
        path: `/api/placements/statuses?${serializePlacementStatusCountsFilter(filter)}`,
        schema: placementStatusCountsSchema,
      }),
    get: (id: string) =>
      api.request({
        path: `/api/placements/${encodeURIComponent(id)}`,
        schema: placementSchema,
      }),
    create: (payload: PlacementMutationPayload) =>
      api.request({
        path: '/api/placements',
        schema: placementMutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    update: ({ id, payload }: { id: string; payload: PlacementMutationPayload }) =>
      api.request({
        path: `/api/placements/${encodeURIComponent(id)}`,
        schema: placementMutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
    setArchived: ({ id, archived }: { id: string; archived: boolean }) =>
      api.request({
        path: `/api/placements/${encodeURIComponent(id)}/${archived ? 'archive' : 'unarchive'}`,
        schema: placementActionResponseSchema,
        init: jsonInit('PUT', {}),
      }),
    listPlacementTargetings: (id: string, filter: PlacementLookupFilter) =>
      api.request({
        path: `/api/placements/${encodeURIComponent(id)}/targetings?${serializePlacementLookupFilter(filter)}`,
        schema: placementTargetingsPageSchema,
      }),
    searchPublishers: (filter: PlacementLookupFilter) => {
      const params = serializePlacementLookupFilter(filter);
      params.delete('searchTerm');
      if (filter.searchTerm.trim()) params.set('name', filter.searchTerm.trim());
      return api.request({
        path: `/api/publishers?${params}`,
        schema: placementLookupPageSchema,
      });
    },
    searchDemandIntegrations: (filter: PlacementLookupFilter) => {
      const params = serializePlacementLookupFilter(filter);
      params.delete('searchTerm');
      if (filter.searchTerm.trim()) params.set('name', filter.searchTerm.trim());
      return api.request({
        path: `/api/dps/integrations?${params}`,
        schema: placementDemandIntegrationsPageSchema,
      });
    },
    searchTargetings: (filter: PlacementLookupFilter) =>
      api.request({
        path: `/api/targetings?${serializePlacementLookupFilter(filter)}`,
        schema: placementTargetingsPageSchema,
      }),
    listIabCategories: () =>
      api.request({
        path: '/api/iab-categories',
        schema: targetingIabCategoriesSchema,
      }),
    listWidgetTypes: () =>
      api.request({
        path: '/api/widget-types?page=0&size=100&sortProperty=name&sortDirection=ASC&enabled=true',
        schema: widgetTypesPageSchema,
      }),
    listPublisherDomains: (publisherId: string) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains?page=0&size=100`,
        schema: publisherDomainsPageSchema,
      }),
    buildAdUnit: (id: string) =>
      api.request({
        path: `/api/placements/ad-unit/${encodeURIComponent(id)}/build`,
        schema: placementActionResponseSchema,
        init: jsonInit('POST', null),
      }),
    getAdUnitConfig: (id: string) =>
      api.request({
        path: `/api/placements/ad-unit/${encodeURIComponent(id)}/config`,
        schema: adUnitConfigSchema,
      }),
  };
}

export type PlacementsClient = ReturnType<typeof createPlacementsClient>;
